// components/InviteAdmin.tsx

import React from "react";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { UserPlus, Info } from "lucide-react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useInviteUser, useDepartments } from "../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { queryClient } from "@shared/api/queries/queryClient";

const InviteAdmin: React.FC = () => {
  const inviteUserMutation = useInviteUser();
  const remote_utility_id = useRemoteUtilityId();

  // const { data: departments, isLoading: departmentsLoading } = useDepartments();

  // Define form fields without dynamic options
  const inviteAdminFields: FormField[] = [
    {
      name: "email",
      type: "email",
      label: "Email Address",
      required: true,
      placeholder: "Admin's email",
      defaultValue: "",
      fullWidth: true,
      pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "fullName",
      type: "text",
      label: "Full Name",
      required: true,
      placeholder: "Full name",
      defaultValue: "",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
        error: "text-red-600 text-sm mt-1",
      },
    },
    // {
    //   name: "department",
    //   type: "select",
    //   label: "Department",
    //   required: true,
    //   placeholder: "Select Department",
    //   defaultValue: "",
    //   fullWidth: true,
    //   classes: {
    //     container: "w-full mb-4",
    //     label: "block text-sm font-medium text-gray-700 mb-2",
    //     select:
    //       "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white cursor-pointer",
    //     error: "text-red-600 text-sm mt-1",
    //   },
    // },
  ];

  // Set dynamic options for 'department' after fetching
  // if (departments?.result) {
  //   console.log("department aa ja ", departments?.result);
  //   const departmentOptions = departments.result
  //     .filter((dept) => dept.isActive)
  //     .map((dept) => ({
  //       value: dept.code,
  //       label: dept.name,
  //       description: `Created by ${dept.createdBy || "System"} on ${
  //         dept.createdDate
  //       }`,
  //     }));

  //   const deptField = inviteAdminFields.find(
  //     (field) => field.name === "department"
  //   );
  //   if (deptField) {
  //     (deptField as any).options = departmentOptions;
  //   }
  // }

  const form = FormService.createForm({
    fields: inviteAdminFields,
    mode: "onChange",
    validateOnMount: false,
  });
  const handleSubmit = async (data: any) => {
    const payload = [
      {
        department: "Management",
        gender: "m",
        role_code: ["Tenant Admin"],
        user: {
          email: data.email,
          name: data.fullName,
          tnc_accepted: true,
        },
        utility: remote_utility_id,
      },
    ];

    inviteUserMutation.mutate(payload, {
      onSuccess: async () => {
        form.reset();

        await queryClient.invalidateQueries({
          queryKey: ["onboarding", "users"],
          exact: false,
        });
      },
    });
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    if (FormService.getFieldError(form, fieldName)) {
      form.clearErrors(fieldName);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.reset();
  };

  // Custom footer with invite button
  const renderCustomFooter = () => (
    <div className="absolute w-full px-4 m-auto mt-6 right-0 left-0 bottom-4">
      <Button
        type="submit"
        disabled={inviteUserMutation.isPending }
        className="w-full transition-all duration-300 ease-in-out"
      >
        {inviteUserMutation.isPending ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            Inviting...
          </>
        ) : (
          <>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite
          </>
        )}
      </Button>
    </div>
  );

  const isLoading = inviteUserMutation.isPending ;

  return (
    <Card className="flex flex-col h-full shadow-sm relative">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center bg-blue-100 text-blue-700 rounded-full p-2 mr-1">
            <UserPlus className="h-5 w-5" />
          </span>
          <span className="font-semibold text-base text-gray-900">
            Invite Admin
          </span>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col justify-between">
        <div>
          <div className="relative rounded bg-muted/50 flex items-center gap-2 px-3 py-2 mb-4 text-xs text-muted-foreground">
            <Info className="h-4 w-4 text-gray-500" />
            <span>
              Invited users will receive an email to activate their admin
              account and set their password.
            </span>
          </div>
            <DynamicForm
              fields={inviteAdminFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={isLoading}
              config={{
                showProgress: false,
                layout: "vertical",
                spacing: "none",
                submitButtonText: "Invite",
                showResetButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-0",
                  fieldContainer: "",
                  buttonContainer: "hidden", // Hide default buttons since we have custom footer
                  fieldGroup: "",
                },
              }}
              renderCustomFooter={renderCustomFooter}
              className=""
              style={{
                background: "transparent",
                border: "none",
                boxShadow: "none",
              }}
            />
          
        </div>
      </CardContent>
    </Card>
  );
};

export default InviteAdmin;
